import { useMemo, useState } from "react";

import { ArrowPathIcon } from "@heroicons/react/24/outline";

import useListingOptimisation from "../../data/useListingOptimisation";
import { IPart } from "../../models/Part";
import { ListingOptimisationStatus } from "../../models/listingOptimisation/IListingOptimisation";
import classNames from "../../utils/classNames";
import { summariseRelistingRules } from "../../utils/relistingRule";
import useNotification from "../notifications/useNotifications";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import BulkRelistResultModal, { BulkRelistResultData } from "../ebayListings/BulkRelistResultModal";
import RelistConfirmModal from "./RelistConfirmModal";

interface IOptimisationPageHeaderProps {
  /** The rows on the current page - the auto-relist badge reads their category rules. */
  rows: IPart[];
}

/**
 * Title, the auto-relist rule badge for the categories on screen, and the two actions that act on
 * everything staged: Discard staged and Send to Relist (with the staged count). Both confirm.
 */
export default function OptimisationPageHeader({ rows }: IOptimisationPageHeaderProps) {
  const { addNotification } = useNotification();
  const { pendingOptimisations, discardChanges, sendToRelist } = useListingOptimisation("OptimisationPageHeader");

  const [discardOpen, setDiscardOpen] = useState(false);
  const [relistOpen, setRelistOpen] = useState(false);
  const [relistResult, setRelistResult] = useState<BulkRelistResultData | null>(null);
  const [relistResultOpen, setRelistResultOpen] = useState(false);

  const stagedIds = useMemo(
    () =>
      (pendingOptimisations.data?.data ?? [])
        .filter((optimisation) => optimisation.status === ListingOptimisationStatus.Pending)
        .map((optimisation) => optimisation.partDataId),
    [pendingOptimisations.data]
  );

  const ruleSummary = useMemo(() => summariseRelistingRules(rows.map((row) => row.relistingRule)), [rows]);

  const handleDiscard = () => {
    setDiscardOpen(false);
    discardChanges.mutate(stagedIds, {
      onSuccess: () => addNotification({ primaryText: "Staged changes discarded", variant: "success" }),
      onError: () => addNotification({ primaryText: "Failed to discard staged changes", variant: "error" }),
    });
  };

  const handleRelist = (appendSku: boolean) => {
    setRelistOpen(false);
    sendToRelist.mutate(
      { partDataIds: stagedIds, appendSku },
      {
        onSuccess: (response) => {
          setRelistResult(response.data);
          setRelistResultOpen(true);
        },
        onError: () => addNotification({ primaryText: "Failed to send listings to be relisted", variant: "error" }),
      }
    );
  };

  const stagedCount = stagedIds.length;

  return (
    <>
      <ConfirmModal
        open={discardOpen}
        setOpen={setDiscardOpen}
        title={"Discard all staged changes?"}
        message={`This removes the staged changes from ${stagedCount} listing${stagedCount === 1 ? "" : "s"}.\nThe live eBay listings are unaffected.`}
        confirmButtonText={"Discard"}
        onConfirm={handleDiscard}
        isLoading={discardChanges.isLoading}
      />

      <RelistConfirmModal
        open={relistOpen}
        setOpen={setRelistOpen}
        count={stagedCount}
        stagedCount={stagedCount}
        onConfirm={handleRelist}
        isLoading={sendToRelist.isLoading}
      />

      <BulkRelistResultModal open={relistResultOpen} setOpen={setRelistResultOpen} result={relistResult} />

      <header className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
        <div className="min-w-0 space-y-1">
          <h1 className="text-2xl font-semibold text-gray-900">eBay Optimisation</h1>
          <p className="text-sm text-gray-600">
            Listings that are not selling, oldest first. Review, stage what you change, then send them to be relisted.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span
              className={classNames(
                "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium",
                ruleSummary
                  ? "border-slate-200 bg-slate-50 text-slate-700"
                  : "border-dashed border-slate-200 bg-white text-slate-500"
              )}
              title={
                ruleSummary
                  ? "The auto-relist rule on the categories shown. Set per category under Admin › Relisting rules."
                  : "None of the categories on this page has an auto-relist rule."
              }
            >
              <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {ruleSummary?.text ?? "No auto-relist rule on these categories"}
            </span>
            <span className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-medium text-sky-800">
              Nothing is sent to eBay until you relist
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setDiscardOpen(true)}
            disabled={stagedCount === 0 || discardChanges.isLoading}
            className="inline-flex h-10 items-center rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Discard staged
          </button>
          <button
            type="button"
            onClick={() => setRelistOpen(true)}
            disabled={stagedCount === 0 || sendToRelist.isLoading}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-green-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sendToRelist.isLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            <span>Send to Relist</span>
            <span
              className={classNames(
                "inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full px-1.5 text-xs font-semibold",
                stagedCount > 0 ? "bg-green-100 text-green-800" : "bg-white/30 text-white"
              )}
              aria-label={`${stagedCount} staged`}
            >
              {pendingOptimisations.isLoading ? "…" : stagedCount}
            </span>
          </button>
        </div>
      </header>
    </>
  );
}
